import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from '../../shared/api.js';
import { keys } from './keys.js';
import { useActiveMe } from './me.js';

/** Rafraîchissement du compteur de non-lus du menu (et au retour sur l'onglet, par TanStack Query). */
export const UNREAD_REFRESH_MS = 30_000;

/**
 * Compteur de messages non lus (`inbox.unreadCount`) : rafraîchi toutes les 30 s et quand l'onglet
 * redevient visible. Désactivé sans `intervention.view` et en session support (aucune lecture propre).
 */
export function useUnreadCount() {
  const me = useActiveMe();
  const enabled = me.active.permissions.includes('intervention.view') && !me.supportSession;
  return useQuery({
    queryKey: keys.unread,
    queryFn: () => api('inbox.unreadCount'),
    enabled,
    refetchInterval: UNREAD_REFRESH_MS,
    refetchOnWindowFocus: 'always',
    staleTime: 10_000,
  });
}

/**
 * Messages du client non lus par ce membre, par dossier (puce « À traiter », 02/10/2026) : première page
 * de la messagerie filtrée sur les non-lus (50 conversations au plus). `InterventionSummary` ne porte pas
 * ce nombre (demande de changement) ; sans `intervention.view` ou en session support : 0 partout.
 */
export function useUnreadByIntervention(): (interventionId: string) => number {
  const me = useActiveMe();
  const enabled = me.active.permissions.includes('intervention.view') && !me.supportSession;
  const list = useQuery({
    queryKey: [...keys.inboxAll, 'unread-by-intervention'],
    queryFn: () => api('inbox.list', { query: { scope: 'open', unread: true, limit: 50 } }),
    enabled,
    staleTime: 15_000,
    refetchInterval: UNREAD_REFRESH_MS,
  });
  return useMemo(() => {
    const map = new Map((list.data?.items ?? []).map((c) => [c.interventionId, c.unreadCount] as const));
    return (id: string) => map.get(id) ?? 0;
  }, [list.data]);
}
